import {
  Component,
  ChangeDetectionStrategy,
  computed,
  effect,
  inject,
  OnDestroy,
  signal,
  viewChild,
} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {
  MatDialog,
  MatDialogConfig,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import {MatIconModule} from '@angular/material/icon';
import {MatButtonModule} from '@angular/material/button';
import {TranslateModule} from '@ngx-translate/core';

import * as APIM from '@common/utils/dist/models/api-messages.js';
import {UtilsPL2 as U} from '@common/utils/dist/utils/utils-pl2.js';

import {UserDetailsStore} from '@stores/user-details.store';
import {EventTrackingStore, EventStatus} from '@stores/event-tracking.store';
import {LoaderStore} from '@stores/loader.store';
import {LoaderModule} from '@directives/loader/loader.module';
import {CloudlabCompleteProfileStep1Component} from './cloudlab-complete-profile-step1.component';
import {CloudlabCompleteProfileStep2Component} from './cloudlab-complete-profile-step2.component';
import {CloudlabCompleteProfileStep3Component} from './cloudlab-complete-profile-step3.component';

@Component({
  standalone: true,
  selector: 'cloudlab-complete-profile-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './cloudlab-complete-profile-dialog.component.html',
  styleUrls: [
    './cloudlab-complete-profile-step.component.scss',
    './cloudlab-complete-profile-dialog.component.scss',
  ],
  imports: [
    MatDialogModule,
    MatIconModule,
    MatButtonModule,
    TranslateModule,
    LoaderModule,
    CloudlabCompleteProfileStep1Component,
    CloudlabCompleteProfileStep2Component,
    CloudlabCompleteProfileStep3Component,
  ],
})
export class CloudlabCompleteProfileDialogComponent implements OnDestroy {
  static readonly DIALOG_CONFIG: MatDialogConfig = {
    width: '600px',
    maxWidth: '100vw',
    maxHeight: '100vh',
    panelClass: 'complete-profile-dialog',
  };

  static open(dialog: MatDialog) {
    return dialog.open(
      CloudlabCompleteProfileDialogComponent,
      CloudlabCompleteProfileDialogComponent.DIALOG_CONFIG,
    );
  }

  private static readonly LOADER_TASK = 'complete-profile-load';

  currentStep = signal(1);
  isLastStep = computed(() => this.currentStep() === 3);
  subtitleKey = computed(
    () => `complete-profile.subtitle${this.currentStep()}`,
  );

  private step1 = viewChild(CloudlabCompleteProfileStep1Component);
  private step2 = viewChild(CloudlabCompleteProfileStep2Component);
  private userDetailsStore = inject(UserDetailsStore);
  private eventTrackingStore = inject(EventTrackingStore);
  private loaderStore = inject(LoaderStore);

  private storeState = toSignal(this.userDetailsStore.state$, {
    initialValue: this.userDetailsStore.state(),
  });
  private eventTrackingState = toSignal(this.eventTrackingStore.state$, {
    initialValue: this.eventTrackingStore.state(),
  });

  loaded = computed(() => {
    if (!U.isEmpty(this.storeState()?.uAt)) return true;
    const events = this.eventTrackingState();
    const hasPendingRead = Object.values(events).some(
      (e) =>
        e.event?.int === APIM.EventIntent.UserDetailsRead &&
        e.status === EventStatus.Pending,
    );
    return !hasPendingRead;
  });

  readonly loaderTask = CloudlabCompleteProfileDialogComponent.LOADER_TASK;

  isCurrentStepValid = computed(() => {
    if (!this.loaded()) return false;
    switch (this.currentStep()) {
      case 1:
        return this.step1()?.isValid() ?? false;
      case 2:
        return this.step2()?.isValid() ?? false;
      default:
        return true;
    }
  });

  constructor(
    public dialogRef: MatDialogRef<CloudlabCompleteProfileDialogComponent>,
  ) {
    effect(() => {
      if (this.loaded()) {
        this.loaderStore.stop();
      } else {
        this.loaderStore.start(
          CloudlabCompleteProfileDialogComponent.LOADER_TASK,
        );
      }
    });
  }

  ngOnDestroy() {
    this.loaderStore.stop();
    this.userDetailsStore.setDoneness(this.storeState().isDone ?? false);
  }

  next() {
    if (!this.isCurrentStepValid()) return;
    this.currentStep.update((s) => s + 1);
  }

  back() {
    this.currentStep.update((s) => s - 1);
  }

  done() {
    this.dialogRef.close(true);
  }
}
